import { createFileRoute } from '@tanstack/react-router'
import { Container } from '../../components/ui/Container'
import { Button } from '../../components/ui/Button'
import { Printer, Zap, Phone, ArrowRight, Award, Clock, BookOpen, Check } from 'lucide-react'
import { generateWhatsAppLink } from '../../lib/utils/whatsapp'

const printServices = [
  {
    id: 'pod',
    title: 'Print on Demand',
    description: 'Cetak buku sesuai pesanan dengan kuantitas fleksibel. Cocok untuk penulis indie dan penerbit kecil.',
    price: 45000,
    minOrder: 1,
    features: [
      'Cetak mulai dari 1 eksemplar',
      'Tanpa minimum order',
      'Proses cepat 3-5 hari kerja',
      'Kualitas tinta pigmentasi',
      'Berbagai pilihan ukuran',
    ],
    icon: Zap,
  },
  {
    id: 'offset',
    title: 'Offset Printing',
    description: 'Solusi cetak massal dengan kualitas premium dan harga ekonomis untuk volume besar.',
    price: 35000,
    minOrder: 500,
    features: [
      'Minimal order 500 eksemplar',
      'Harga lebih ekonomis per unit',
      'Kualitas cetakan premium',
      'Pilihan finishing lengkap',
      'Konsultasi desain gratis',
    ],
    icon: Printer,
  },
]

const paperTypes = [
  {
    name: 'Book Paper 57gsm',
    description: 'Kertas novel standar, ringan dan ekonomis',
    ideal: 'Novel, fiksi, buku teks',
    color: 'Cream',
  },
  {
    name: 'Book Paper 72gsm',
    description: 'Kertas novel premium, lebih tebal dan nyaman dibaca',
    ideal: 'Novel premium, buku literatur',
    color: 'Cream',
  },
  {
    name: 'HVS 70gsm',
    description: 'Kertas standar putih untuk buku non-fiksi',
    ideal: 'Buku akademik, textbook',
    color: 'Putih',
  },
  {
    name: 'HVS 80gsm',
    description: 'Kertas putih premium dengan tekstur halus',
    ideal: 'Buku premium, portfolio',
    color: 'Putih',
  },
]

const steps = [
  {
    number: '01',
    title: 'Konsultasi',
    description: 'Diskusikan kebutuhan cetak Anda dengan tim kami untuk mendapatkan rekomendasi terbaik.',
  },
  {
    number: '02',
    title: 'Konfirmasi',
    description: 'Setujui spesifikasi, harga, dan timeline pengerjaan yang telah disepakati.',
  },
  {
    number: '03',
    title: 'Produksi',
    description: 'Tim kami memproses cetakan dengan kualitas terbaik dan kontrol ketat.',
  },
  {
    number: '04',
    title: 'Pengiriman',
    description: 'Buku dicetak dan dikirim ke alamat Anda dengan packaging aman.',
  },
]

const benefits = [
  {
    title: 'Kualitas Premium',
    description: 'Menggunakan mesin cetak modern dengan tinta berkualitas tinggi',
    icon: Award,
  },
  {
    title: 'Tepat Waktu',
    description: 'Komitmen pengerjaan sesuai timeline yang disepakati',
    icon: Clock,
  },
  {
    title: 'Berpengalaman',
    description: 'Telah melayani ratusan penulis dan penerbit sejak 2018',
    icon: BookOpen,
  },
]

export const Route = createFileRoute('/jasa-cetak/')({
  component: JasaCetak,
})

function handleServiceOrder(serviceName: string, price: number) {
  const message = `Halo, saya tertarik dengan layanan ${serviceName} (mulai dari Rp ${price.toLocaleString('id-ID')}). Mohon info lebih lanjut tentang spesifikasi dan cara pemesanan.`
  const link = generateWhatsAppLink(message)
  window.open(link, '_blank')
}

function JasaCetak() {
  return (
    <div className="min-h-screen bg-white">
    <section className="relative overflow-hidden bg-brand-slate-800">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-yellow-400 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-slate-700 rounded-full blur-3xl" />
      </div>

      <Container className="relative z-10 py-20 lg:py-28">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="max-w-xl">
              <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-400 uppercase mb-6 block">
                Layanan Cetak
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white leading-[1.1] mb-6">
                Jasa Cetak Buku{' '}
                <span className="font-medium">Berkualitas</span>
              </h1>
              <p className="text-lg text-brand-slate-400 leading-relaxed mb-8">
                Layanan percetakan profesional dengan teknologi Print on Demand dan Offset untuk penulis, penerbit, dan institusi.
              </p>

              <Button
                onClick={() => handleServiceOrder('Konsultasi Cetak', 0)}
                className="bg-brand-yellow-400 hover:bg-brand-yellow-500 text-brand-slate-900 px-8 py-3 rounded-full font-medium transition-all cursor-pointer"
              >
                Mulai Konsultasi
              </Button>
            </div>

            {/* Right Image - Clean Rectangular Frame */}
            <div className="relative">
              {/* Single Frame Container */}
              <div className="bg-white p-2">
                <img
                  src="/mesin-offset.jpg"
                  alt="Mesin Cetak Offset"
                  className="w-full h-auto object-cover aspect-4/3"
                />
              </div>

              {/* Simple Label Below */}
              <div className="mt-3 flex items-center gap-2">
                <div className="w-8 h-px bg-brand-yellow-400" />
                <span className="text-xs text-brand-slate-400 tracking-wide">Mesin Offset Heidelberg</span>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>

      {/* Print Services Section */}
      <section className="py-20 lg:py-28 bg-white">
        <Container>
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-600 uppercase mb-4 block">
                Pilihan Layanan
              </span>
              <h2 className="text-3xl lg:text-4xl font-light text-brand-slate-900 mb-6">
                Solusi Cetak untuk <span className="font-medium">Setiap Kebutuhan</span>
              </h2>
              <p className="text-brand-slate-500 max-w-2xl mx-auto leading-relaxed">
                Pilih metode cetak yang sesuai dengan kebutuhan dan budget Anda. Konsultasikan dengan tim kami untuk rekomendasi terbaik.
              </p>
            </div>

            {/* Services Grid - 2 Columns */}
            <div className="grid md:grid-cols-2 gap-6">
              {printServices.map((service, index) => {
                const Icon = service.icon
                return (
                  <div
                    key={service.id}
                    className={`group relative bg-white rounded-2xl p-8 transition-all duration-300 hover:shadow-xl border ${
                      service.id === 'offset'
                        ? 'border-brand-yellow-400 ring-1 ring-brand-yellow-400'
                        : 'border-brand-slate-200 hover:border-brand-yellow-200'
                    }`}
                  >
                    {/* Recommended Badge */}
                    {service.id === 'offset' && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-brand-yellow-400 text-brand-slate-900 text-xs font-semibold px-4 py-1.5 rounded-full tracking-wide">
                          DIREKOMENDASIKAN
                        </span>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-12 h-12 bg-brand-slate-50 group-hover:bg-brand-yellow-100 rounded-xl flex items-center justify-center transition-colors">
                        <Icon className="w-6 h-6 text-brand-slate-600 group-hover:text-brand-yellow-600" strokeWidth={1.5} />
                      </div>
                      <span className="text-4xl font-light text-brand-slate-100 group-hover:text-brand-yellow-100 transition-colors tabular-nums">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-medium text-brand-slate-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-brand-slate-500 mb-12 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm">
                          <Check className="w-4 h-4 text-brand-yellow-500 mt-0.5 shrink-0" strokeWidth={1.5} />
                          <span className="text-brand-slate-600">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <Button
                      onClick={() => handleServiceOrder(service.title, service.price)}
                      className={`w-full h-11 rounded-full font-medium transition-all cursor-pointer ${
                        service.id === 'offset'
                          ? 'bg-brand-yellow-400 hover:bg-brand-yellow-500 text-brand-slate-900'
                          : 'bg-brand-slate-900 hover:bg-brand-slate-800 text-white'
                      }`}
                    >
                      Pilih Layanan
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* Paper Types - Editorial Grid */}
      <section className="py-20 lg:py-28 bg-brand-slate-50">
        <Container>
          <div className="max-w-6xl mx-auto">
            {/* Header Center */}
            <div className="max-w-2xl mx-auto text-center mb-16">
              <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-600 uppercase mb-4 block">
                Pilihan Kertas
              </span>
              <h2 className="text-3xl lg:text-4xl font-light text-brand-slate-900 mb-6">
                Jenis Kertas <span className="font-medium">Berkualitas</span>
              </h2>
              <p className="text-brand-slate-500 leading-relaxed">
                Pilihan kertas terbaik untuk hasil cetak yang optimal dan nyaman dibaca
              </p>
            </div>

            {/* Paper Grid - Editorial Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paperTypes.map((paper, index) => (
                <div
                  key={paper.name}
                  className="group bg-white rounded-xl p-6 border border-brand-slate-200 hover:border-brand-yellow-300 transition-all duration-300 hover:shadow-md"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl font-light text-brand-slate-200 group-hover:text-brand-yellow-400 transition-colors tabular-nums w-12 shrink-0">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-medium text-brand-slate-900">
                          {paper.name}
                        </h3>
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-brand-slate-100 rounded-full">
                          <div className={`w-2 h-2 rounded-full ${paper.color === 'Cream' ? 'bg-amber-100 border border-amber-300' : 'bg-white border border-gray-300'}`} />
                          <span className="text-[10px] uppercase tracking-wider text-brand-slate-500">{paper.color}</span>
                        </div>
                      </div>
                      <p className="text-sm text-brand-slate-500 mb-2">{paper.description}</p>
                      <p className="text-xs text-brand-slate-400">Ideal untuk: <span className="text-brand-slate-600">{paper.ideal}</span></p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Process Section - Editorial Timeline */}
      <section className="py-20 lg:py-28 bg-white">
        <Container>
          <div className="max-w-6xl mx-auto">
            {/* Header Center */}
            <div className="max-w-2xl mx-auto text-center mb-16">
              <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-600 uppercase mb-4 block">
                Alur Kerja
              </span>
              <h2 className="text-3xl lg:text-4xl font-light text-brand-slate-900 mb-6">
                Proses Cetak yang <span className="font-medium">Mudah</span>
              </h2>
              <p className="text-brand-slate-500 leading-relaxed">
                Hanya 4 langkah mudah untuk mewujudkan buku Anda
              </p>
            </div>

            {/* Process Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <div key={step.number} className="relative group">
                  {/* Connector */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-brand-slate-200 z-0" />
                  )}

                  <div className="relative bg-brand-slate-50 rounded-2xl p-6 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-brand-yellow-200 h-full">
                    {/* Step Number - Top Right with fixed width */}
                    <div className="flex justify-between items-start mb-6">
                      <div className="w-8 h-0.5 bg-brand-yellow-400 mt-3" />
                      <span className="text-4xl font-light text-brand-slate-200 group-hover:text-brand-yellow-100 transition-colors tabular-nums leading-none">
                        {step.number}
                      </span>
                    </div>

                    <h3 className="text-lg font-medium text-brand-slate-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm text-brand-slate-500 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Benefits Section - Editorial Grid */}
      <section className="py-20 lg:py-28 bg-brand-slate-900 text-white">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left - Large Typography */}
              <div>
                <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-400 uppercase mb-6 block">
                  Keunggulan
                </span>
                <h2 className="text-4xl lg:text-5xl font-light leading-[1.1] mb-8">
                  Mengapa Memilih{' '}
                  <span className="font-medium block mt-2">Layanan Cetak Kami?</span>
                </h2>
                <p className="text-brand-slate-400 text-lg leading-relaxed mb-8 max-w-md">
                  Kami berkomitmen memberikan layanan cetak terbaik dengan standar kualitas tinggi dan pelayanan profesional.
                </p>

                <Button
                  onClick={() => handleServiceOrder('Konsultasi Cetak', 0)}
                  className="bg-brand-yellow-400 hover:bg-brand-yellow-500 text-brand-slate-900 px-8 py-3 rounded-full font-medium transition-all cursor-pointer"
                >
                  Mulai Konsultasi
                </Button>
              </div>

              {/* Right - Benefits List */}
              <div className="space-y-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon
                  return (
                    <div key={benefit.title} className="flex gap-6 group">
                      <div className="shrink-0">
                        <span className="text-4xl font-light text-brand-slate-700 group-hover:text-brand-yellow-400 transition-colors tabular-nums">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>
                      <div className="pb-8 border-b border-brand-slate-800 last:border-0 last:pb-0 flex-1">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-brand-slate-800 group-hover:bg-brand-yellow-400 flex items-center justify-center transition-colors shrink-0">
                            <Icon className="w-5 h-5 text-brand-slate-400 group-hover:text-brand-slate-900" strokeWidth={1.5} />
                          </div>
                          <div>
                            <h3 className="text-xl font-medium text-white mb-2">
                              {benefit.title}
                            </h3>
                            <p className="text-brand-slate-400 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section - Editorial */}
      <section className="py-20 lg:py-28 bg-white">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-brand-slate-50 p-12 lg:p-20 border border-brand-slate-100">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow-200 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />

              <div className="relative z-10 text-center max-w-2xl mx-auto">
                <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-600 uppercase mb-4 block">
                  Mulai Sekarang
                </span>
                <h2 className="text-3xl lg:text-4xl font-light text-brand-slate-900 mb-6">
                  Siap Mencetak Buku Anda?
                </h2>
                <p className="text-brand-slate-500 mb-8 leading-relaxed">
                  Konsultasikan kebutuhan cetak Anda dengan tim ahli kami. Gratis konsultasi dan penawaran harga terbaik.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => handleServiceOrder('Jasa Cetak Buku', 35000)}
                    className="h-12 px-8 bg-brand-slate-900 hover:bg-brand-yellow-400 hover:text-brand-slate-900 text-white font-medium rounded-full transition-all cursor-pointer"
                  >
                    <Phone className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Hubungi via WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 px-8 border-brand-slate-300 text-brand-slate-700 hover:bg-brand-slate-100 rounded-full transition-all cursor-pointer"
                  >
                    Lihat Portfolio
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  )
}
